import {Injectable} from "@angular/core";
import {CountryModel} from "../types/country";
import {RegionsByCountryModel, RegionModel, regionsByCountry} from "../types/region";
import {GeoRegionModel} from "../types/geo-region";
import {ContactLocaleModel} from "../types/contact-locale";
import {HttpClient} from "@angular/common/http";
import {BehaviorSubject, Observable, Subject} from "rxjs";
import {map} from "rxjs/operators";
import {AppSettingsModel} from "./app-settings.model";
import {AuthDetailModel} from "./auth-detail.model";
import {AppSettingsResponseModel} from "./app-settings-response.model";

@Injectable({providedIn: "root"})
export class AppSettingsService {
  private settingsSubject: Subject<AppSettingsModel> = new BehaviorSubject<AppSettingsModel>(new AppSettingsModel());
  private userSubject: Subject<AuthDetailModel> = new BehaviorSubject<AuthDetailModel>(new AuthDetailModel());
  private contactLocalesSubject: Subject<ContactLocaleModel[]> = new BehaviorSubject<ContactLocaleModel[]>([]);
  private countriesSubject: Subject<CountryModel[]> = new BehaviorSubject<CountryModel[]>([]);
  private geoRegionsSubject: Subject<GeoRegionModel[]> = new BehaviorSubject<GeoRegionModel[]>([]);
  private regionsSubject: Subject<RegionModel[]> = new BehaviorSubject<RegionModel[]>([]);
  private regionsByCountrySubject: Subject<RegionsByCountryModel> = new BehaviorSubject<RegionsByCountryModel>({});

  /**
   * Get env.settings
   * @type {Observable<AppSettingsModel>}
   */
  settings: Observable<AppSettingsModel> = this.settingsSubject.asObservable();

  /**
   * Get logged-in user
   * @type {Observable<AuthDetailModel>}
   */
  user: Observable<AuthDetailModel> = this.userSubject.asObservable();

  /**
   * Get list of supported countries (for countries dropdown)
   * @type {Observable<CountryModel[]>}
   */
  countries: Observable<CountryModel[]> = this.countriesSubject.asObservable();

  /**
   * Get list of supported geo-regions (for geo-regions dropdown)
   * @type {Observable<CountryModel[]>}
   */
  geoRegions: Observable<GeoRegionModel[]> = this.geoRegionsSubject.asObservable();

  /**
   * Get list of supported geo-regions (for geo-regions dropdown)
   * @type {Observable<CountryModel[]>}
   */
  regions: Observable<RegionModel[]> = this.regionsSubject.asObservable();
  regionsByCountry: Observable<RegionsByCountryModel> = this.regionsByCountrySubject.asObservable();

  /**
   * Get list of supported contact-locales (for contact-locale dropdown)
   * @type {Observable<ContactLocaleModel[]>}
   */
  contactLocales: Observable<ContactLocaleModel[]> = this.contactLocalesSubject.asObservable();

  /**
   * Run to load data from the server.
   * Should be run at app launch or at other times if deemed useful.
   * @returns {Promise<void>}
   */
  async load() {
    const {
      settings,
      countries,
      user,
      geoRegions,
      regions,
      contactLocales,
    } = await this.http.get<AppSettingsResponseModel>("/api/settings")
      .pipe(map(it => new AppSettingsResponseModel(it)))
      .toPromise();

    this.userSubject.next(user);
    this.settingsSubject.next(settings);
    this.countriesSubject.next(countries);
    this.geoRegionsSubject.next(geoRegions);
    this.regionsSubject.next(regions);
    this.regionsByCountrySubject.next(regionsByCountry(regions));
    this.contactLocalesSubject.next(contactLocales);
  }

  constructor(private http: HttpClient) {}
}
